import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getEquipmentAreas, getWorkOrder, createWorkOrder, updateWorkOrder, deleteWorkOrder, getIssue, updateIssue } from '../services/maintenanceService';
import { getTeamMembers } from '../services/teamService';
import { uploadPhoto } from '../services/photoService';
import WorkOrderPhotos from '../components/WorkOrderPhotos';
import { useLocationContext } from '../contexts/LocationContext';
import { useEquipmentGroups } from '../utils/equipmentGroups';
import { track } from '../services/analyticsService';


// Local ID generator for pending photos (browser-only, before they exist server-side).
const genPendingId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const MaintenanceWorkOrderForm = () => {
  const equipmentCategories = useEquipmentGroups();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { currentLocation } = useLocationContext();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    type: searchParams.get('type') || 'task',
    equipment_area_id: '',
    equipment_categories: [],
    priority: 'normal',
    status: 'open',
    due_date: '',
    completed_date: '',
    description: '',
    parts_cost: '',
    labor_cost: '',
    vendor: '',
    performed_by: '',
    assigned_to_user_id: ''
  });
  const fromIssueId = searchParams.get('from_issue');
  const [equipmentMode, setEquipmentMode] = useState('unit'); // 'unit' or 'group'
  const [equipmentAreas, setEquipmentAreas] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pending photos held in local state until the work order is created and
  // has an id we can attach them to. Only used in the "new" flow.
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{file, previewUrl, id}]
  const pendingInputRef = React.useRef(null);

  useEffect(() => {
    fetchEquipmentAreas();
    fetchTeamMembers();
    if (isEditing) {
      fetchWorkOrder();
    } else if (fromIssueId) {
      fetchFromIssue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentLocation?.id]);

  // Free the blob URLs when the component unmounts.
  useEffect(() => {
    return () => {
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddPending = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const items = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      id: genPendingId(),
    }));
    setPendingPhotos((prev) => [...prev, ...items]);
    e.target.value = '';
  };

  const removePending = (pendingId) => {
    setPendingPhotos((prev) => {
      const found = prev.find((p) => p.id === pendingId);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.id !== pendingId);
    });
  };

  const fetchTeamMembers = async () => {
    try {
      const data = await getTeamMembers();
      setTeamMembers(data.members || []);
    } catch (err) {
      // Non-critical
    }
  };

  const fetchFromIssue = async () => {
    try {
      setLoading(true);
      const issue = await getIssue(fromIssueId);
      const cats = issue.equipment_category || [];
      setFormData(prev => ({
        ...prev,
        type: 'repair',
        description: issue.description || '',
        equipment_area_id: issue.equipment_area_id || '',
        equipment_categories: cats,
      }));
      if (cats.length > 0) {
        setEquipmentMode('group');
      } else {
        setEquipmentMode('unit');
      }
    } catch (err) {
      // Non-critical — form still usable
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentAreas = async () => {
    try {
      const data = await getEquipmentAreas();
      setEquipmentAreas(data.equipment_areas);
    } catch (err) {
      // Non-critical
    }
  };

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const wo = await getWorkOrder(id);
      const cats = wo.equipment_category || [];
      setFormData({
        title: wo.title,
        type: wo.type,
        equipment_area_id: wo.equipment_area_id || '',
        equipment_categories: cats,
        priority: wo.priority,
        status: wo.status,
        due_date: wo.due_date || '',
        completed_date: wo.completed_date || '',
        description: wo.description || '',
        parts_cost: wo.parts_cost || '',
        labor_cost: wo.labor_cost || '',
        vendor: wo.vendor || '',
        performed_by: wo.performed_by || '',
        assigned_to_user_id: wo.assigned_to_user_id || ''
      });
      // Set mode based on what data exists
      if (cats.length > 0) {
        setEquipmentMode('group');
      } else {
        setEquipmentMode('unit');
      }
    } catch (err) {
      setError('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-sync: setting status to completed defaults completed_date to today
      if (name === 'status' && value === 'completed' && !prev.completed_date) {
        updated.completed_date = new Date().toISOString().split('T')[0];
      }
      // Auto-sync: setting a completed_date auto-sets status to completed
      if (name === 'completed_date' && value && prev.status !== 'completed') {
        updated.status = 'completed';
      }
      return updated;
    });
  };

  const toggleCategory = (catValue) => {
    setFormData(prev => {
      const cats = prev.equipment_categories;
      if (cats.includes(catValue)) {
        return { ...prev, equipment_categories: cats.filter(c => c !== catValue) };
      } else {
        return { ...prev, equipment_categories: [...cats, catValue] };
      }
    });
  };

  const handleModeChange = (mode) => {
    setEquipmentMode(mode);
    // Clear the other mode's selection
    if (mode === 'unit') {
      setFormData(prev => ({ ...prev, equipment_categories: [] }));
    } else {
      setFormData(prev => ({ ...prev, equipment_area_id: '' }));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this work order? This cannot be undone.')) return;
    try {
      await deleteWorkOrder(id);
      navigate('/maintenance');
    } catch (err) {
      setError('Failed to delete work order');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        title: formData.title,
        type: formData.type,
        priority: formData.priority,
        status: formData.status,
        description: formData.description,
        due_date: formData.due_date || null,
        completed_date: formData.completed_date || null,
        parts_cost: parseFloat(formData.parts_cost) || 0,
        labor_cost: parseFloat(formData.labor_cost) || 0,
        vendor: formData.vendor,
        performed_by: formData.performed_by,
        assigned_to_user_id: formData.assigned_to_user_id || null,
        equipment_area_id: equipmentMode === 'unit' ? (formData.equipment_area_id || null) : null,
        equipment_category: equipmentMode === 'group' && formData.equipment_categories.length > 0
          ? formData.equipment_categories
          : null,
      };

      if (isEditing) {
        await updateWorkOrder(id, payload);
      } else {
        track('work_order_create');
        const created = await createWorkOrder(payload);
        if (fromIssueId && created?.id) {
          try {
            await updateIssue(fromIssueId, { work_order_id: created.id });
          } catch (linkErr) {
            console.error('Failed to link issue to work order', linkErr);
          }
        }
        // Upload pending photos now that we have a work order id.
        if (created?.id && pendingPhotos.length > 0) {
          let failures = 0;
          for (const p of pendingPhotos) {
            try {
              await uploadPhoto(p.file, {
                contextType: 'work_order',
                contextId: created.id,
              });
            } catch (uploadErr) {
              console.error('Photo upload failed for', p.file.name, uploadErr);
              failures += 1;
            }
          }
          if (failures > 0) {
            // Non-blocking — the work order is already saved. Surface the
            // count so the user can retry from the edit page if needed.
            setError(
              `Work order saved, but ${failures} of ${pendingPhotos.length} photo(s) failed to upload. ` +
              `You can retry from the edit page.`
            );
            setSaving(false);
            return;
          }
        }
      }
      navigate('/maintenance');
    } catch (err) {
      setError('Failed to save work order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Group equipment areas by category for optgroup
  const groupedAreas = equipmentAreas.reduce((acc, area) => {
    if (!acc[area.category]) acc[area.category] = [];
    acc[area.category].push(area);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Work Order' : 'New Work Order'}
        </h1>
        <button
          onClick={() => navigate('/maintenance')}
          className="text-gray-600 hover:text-gray-800 text-sm font-medium"
        >
          <span className="hidden sm:inline">Back to Maintenance</span>
          <span className="sm:hidden">Back</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 overflow-hidden max-w-full">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. Replace coin acceptor, Fix leak in Bay 2"
          />
        </div>

        {/* Type + Priority row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="task">Task</option>
              <option value="repair">Repair</option>
              <option value="scheduled">Scheduled</option>
              <option value="capex">Capex</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        {/* Equipment Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
          <div className="flex items-center gap-4 mb-3">
            <button
              type="button"
              onClick={() => handleModeChange('unit')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                equipmentMode === 'unit'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Specific Unit
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('group')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                equipmentMode === 'group'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Equipment Group(s)
            </button>
          </div>

          {equipmentMode === 'unit' ? (
            <select
              name="equipment_area_id"
              value={formData.equipment_area_id}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">General (no specific area)</option>
              {Object.entries(groupedAreas).map(([category, areas]) => (
                <optgroup key={category} label={category}>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>{area.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          ) : (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {equipmentCategories.map(cat => (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      formData.equipment_categories.includes(cat.value)
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.equipment_categories.includes(cat.value)}
                      onChange={() => toggleCategory(cat.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium">{cat.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Select all equipment groups this work applies to.
              </p>
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Due Date + Completed Date row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              style={{ WebkitAppearance: 'none', maxWidth: '100%', boxSizing: 'border-box', minHeight: '42px' }}
              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Completed Date</label>
            <input
              type="date"
              name="completed_date"
              value={formData.completed_date}
              onChange={handleChange}
              style={{ WebkitAppearance: 'none', maxWidth: '100%', boxSizing: 'border-box', minHeight: '42px' }}
              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Details about the work needed or completed..."
          />
        </div>

        {/* Cost fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parts Cost ($)</label>
            <input
              type="number"
              name="parts_cost"
              value={formData.parts_cost}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Labor Cost ($)</label>
            <input
              type="number"
              name="labor_cost"
              value={formData.labor_cost}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
            />
          </div>
        </div>

        {(parseFloat(formData.parts_cost) > 0 || parseFloat(formData.labor_cost) > 0) && (
          <div className="text-sm text-gray-600">
            Total Cost: <span className="font-semibold">${((parseFloat(formData.parts_cost) || 0) + (parseFloat(formData.labor_cost) || 0)).toFixed(2)}</span>
          </div>
        )}

        {/* Vendor + Assigned To */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <input
              type="text"
              name="vendor"
              value={formData.vendor}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. Kleen-Rite, local plumber"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            {teamMembers.length > 0 ? (
              <select
                name="assigned_to_user_id"
                value={formData.assigned_to_user_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user?.display_name || m.user?.phone_number}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="performed_by"
                value={formData.performed_by}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. Self, contractor name"
              />
            )}
          </div>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Work Order' : 'Create Work Order')}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/maintenance')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {isEditing ? (
        <div className="mt-6">
          <WorkOrderPhotos workOrderId={id} />
        </div>
      ) : (
        <div className="mt-6 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Photos ({pendingPhotos.length})</h3>
              <p className="text-xs text-gray-500">Upload happens after you click Create Work Order.</p>
            </div>
            <label className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium cursor-pointer">
              + Add photo
              <input
                ref={pendingInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleAddPending}
                className="hidden"
              />
            </label>
          </div>
          {pendingPhotos.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No photos queued. Add one to document the problem.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {pendingPhotos.map((p) => (
                <div key={p.id} className="relative group aspect-square bg-gray-100 rounded overflow-hidden">
                  <img
                    src={p.previewUrl}
                    alt={p.file.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // HEIC previews fail on non-Safari browsers — fall back
                      // to a filename card so the pending state is still visible.
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div
                    className="absolute inset-0 items-center justify-center bg-gray-100 text-xs text-gray-500 text-center p-2 break-all"
                    style={{ display: 'none' }}
                  >
                    {p.file.name}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePending(p.id)}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                    title="Remove photo"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MaintenanceWorkOrderForm;
